import { QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { initAnalytics } from './analytics/posthog';
import { captureUtm } from './analytics/utm';
import { createQueryClient } from './api/query-client';
import './index.css';
import App from './App.tsx';

// Devtools are a devDependency and lazily imported, so they are never pulled into
// the production bundle.
const Devtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null;

// At module scope, beside the query client, and not in an effect: StrictMode
// runs effects twice in development, and a second `posthog.init` is a second
// set of listeners on one page.
initAnalytics();
// Off the landing URL, before the router redirects anywhere and drops it.
captureUtm(window.location.search);

const queryClient = createQueryClient();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* nuqs holds URL state (the admin grids' page/sort/search/filters), so
            every view is a link somebody can paste. Inside BrowserRouter: the
            adapter reads the router's own location. */}
        <NuqsAdapter>
          <App />
        </NuqsAdapter>
      </BrowserRouter>
      {Devtools ? (
        <Suspense fallback={null}>
          {/* bottom-left because bottom-right is product UI: the capture rail's
            footer on /browse and the canvas zoom controls both live there, and
            the floating devtools button sits on top of them and eats clicks. */}
          {/* Never printed — a report saved as a PDF in dev would carry it. */}
          <div className="print:hidden">
            <Devtools buttonPosition="bottom-left" initialIsOpen={false} />
          </div>
        </Suspense>
      ) : null}
    </QueryClientProvider>
  </StrictMode>,
);
